import React, { useEffect, useState } from "react";
import { get, post, put, remove } from "../../redux/ApiHelper";
import { errorToast, successToast } from "../../utils/toast.util";
import { QuestionInterface } from "../../interfaces/question.interface";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    IconButton,
    Chip,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import TableComponent from "../../components/table/TableComponent";
import { Column } from "../../interfaces/table.interface";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

const Questions = () => {
    const [questions, setQuestions] = useState<QuestionInterface[]>([]);
    const [filteredQuestions, setFilteredQuestions] = useState<QuestionInterface[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [editingQuestion, setEditingQuestion] = useState<QuestionInterface | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [subjects, setSubjects] = useState<string[]>([]);
    const { signedAdmin } = useSelector((state: RootState) => state.admin);

    // Form state
    const [formData, setFormData] = useState<Partial<QuestionInterface & { adminEmail: string; adminRole: string; adminType: string }>>({
        subject: "",
        class: "",
        questionType: "quiz",
        question: "",
        questionImageUrl: "",
        marks: 0,
        negativeMarks: 0,
        difficultyLevel: "medium",
        options: [],
        correctOptionIndexes: [],
        answerString: "",
    });

    // Validation errors
    const [errors, setErrors] = useState<{
        subject?: string;
        class?: string;
        questionType?: string;
        question?: string;
        marks?: string;
        negativeMarks?: string;
        options?: string;
        correctOptionIndexes?: string;
        answerString?: string;
    }>({});

    useEffect(() => {
        fetchQuestions();
        fetchSubjects();
    }, []);

    useEffect(() => {
        const lowerQuery = searchQuery.toLowerCase();
        const filtered = questions.filter(
            (q) =>
                q.question?.toLowerCase().includes(lowerQuery) ||
                q.subject?.toLowerCase().includes(lowerQuery) ||
                q.class?.toLowerCase().includes(lowerQuery) ||
                q.questionType?.toLowerCase().includes(lowerQuery)
        );
        setFilteredQuestions(filtered);
    }, [questions, searchQuery]);

    const fetchQuestions = async () => {
        try {
            setLoading(true);
            const response = await get("/admin/examination/questions");
            setQuestions(response.data || []);
            setFilteredQuestions(response.data || []);
        } catch (error) {
            console.error("Error fetching questions:", error);
            errorToast("Failed to fetch questions");
        } finally {
            setLoading(false);
        }
    };

    const fetchSubjects = async () => {
        try {
            const response = await get("/admin/subject/get-all");
            setSubjects(response.subjects || []);
        } catch (error) {
            console.error("Error fetching subjects:", error);
        }
    };


    const handleOpenModal = (question?: QuestionInterface) => {
        if (question) {
            setEditingQuestion(question);
            setFormData({
                subject: question.subject,
                class: question.class,
                questionType: question.questionType,
                question: question.question,
                questionImageUrl: question.questionImageUrl || "",
                marks: question.marks,
                negativeMarks: question.negativeMarks || 0,
                difficultyLevel: question.difficultyLevel,
                options: question.options || [],
                correctOptionIndexes: question.correctOptionIndexes || [],
                answerString: question.answerString || "",
                adminEmail: signedAdmin.email,
                adminRole: signedAdmin.role,
                adminType: signedAdmin.type,
            });
        } else {
            setEditingQuestion(null);
            setFormData({
                subject: "",
                class: "",
                questionType: "quiz",
                question: "",
                questionImageUrl: "",
                marks: 0,
                negativeMarks: 0,
                difficultyLevel: "medium",
                options: [],
                correctOptionIndexes: [],
                answerString: "",
                adminEmail: signedAdmin.email,
                adminRole: signedAdmin.role,
                adminType: signedAdmin.type,
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setEditingQuestion(null);
        setErrors({});
    };

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        if (!formData.subject || formData.subject.trim() === "") {
            newErrors.subject = "Subject is required";
        }
        if (!formData.class || String(formData.class).trim() === "") {
            newErrors.class = "Class is required";
        }

        if (!formData.questionType) {
            newErrors.questionType = "Question type is required";
        }

        if (!formData.question || formData.question.trim() === "") {
            newErrors.question = "Question is required";
        }

        if (!formData.marks || formData.marks <= 0) {
            newErrors.marks = "Marks must be greater than 0";
        } else if (formData.marks < 0) {
            newErrors.marks = "Marks cannot be negative";
        }

        if (formData.negativeMarks && formData.negativeMarks < 0) {
            newErrors.negativeMarks = "Negative marks cannot be negative";
        }

        if (formData.questionType === "quiz") {
            if (!formData.options || formData.options.length < 2) {
                newErrors.options = "Quiz questions must have at least 2 options";
            } else {
                // Check if any option is empty
                const hasEmptyOption = formData.options.some(opt => !opt.optionText || opt.optionText.trim() === "");
                if (hasEmptyOption) {
                    newErrors.options = "All options must have text";
                }
            }
            if (!formData.correctOptionIndexes || formData.correctOptionIndexes.length === 0) {
                newErrors.correctOptionIndexes = "Please select at least one correct option";
            }
        } else if (formData.questionType === "subjective") {
            if (!formData.answerString || formData.answerString.trim() === "") {
                newErrors.answerString = "Answer string is required for subjective questions";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            if (editingQuestion?._id) {
                await put(`/admin/examination/question/${editingQuestion._id}`, formData);
                successToast("Question updated successfully");
            } else {
                await post("/admin/examination/question/create", formData);
                successToast("Question created successfully");
            }
            handleCloseModal();
            fetchQuestions();
        } catch (error) {
            console.error("Error saving question:", error);
            errorToast("Failed to save question");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (questionId: string) => {
        if (!window.confirm("Are you sure you want to delete this question?")) {
            return;
        }

        try {
            setLoading(true);
            await remove(`/admin/examination/question/${questionId}`);
            successToast("Question deleted successfully");
            fetchQuestions();
        } catch (error) {
            console.error("Error deleting question:", error);
            errorToast("Failed to delete question");
        } finally {
            setLoading(false);
        }
    };

    const handleAddOption = () => {
        setFormData({
            ...formData,
            options: [...(formData.options || []), { optionText: "" }],
        });
        if (errors.options) {
            setErrors({ ...errors, options: undefined });
        }
    };

    const handleUpdateOption = (index: number, value: string) => {
        const newOptions = [...(formData.options || [])];
        newOptions[index] = { ...newOptions[index], optionText: value };
        setFormData({ ...formData, options: newOptions });
        if (errors.options) {
            setErrors({ ...errors, options: undefined });
        }
    };

    const handleRemoveOption = (index: number) => {
        const newOptions = formData.options?.filter((_, i) => i !== index) || [];
        setFormData({ ...formData, options: newOptions });
        if (errors.options) {
            setErrors({ ...errors, options: undefined });
        }
    };

    const handleToggleCorrectOption = (index: number) => {
        const currentIndexes = formData.correctOptionIndexes || [];
        const newIndexes = currentIndexes.includes(index)
            ? currentIndexes.filter((i) => i !== index)
            : [...currentIndexes, index];
        setFormData({ ...formData, correctOptionIndexes: newIndexes });
    };

    const columns: Column[] = [
        { select: "subject", header: "Subject", align: "left" },
        { select: "class", header: "Class", align: "center" },
        {
            select: "questionType",
            header: "Type",
            render: (value: string) => (
                <Chip
                    label={value}
                    color={value === "quiz" ? "primary" : "secondary"}
                    size="small"
                />
            ),
        },
        {
            select: "question",
            header: "Question",
            align: "left",
            render: (value: string) => (
                <Typography variant="body2" sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {value}
                </Typography>
            ),
        },
        { select: "marks", header: "Marks" },
        {
            select: "difficultyLevel",
            header: "Difficulty",
            render: (value: string) => (
                <Chip
                    label={value}
                    color={
                        value === "easy" ? "success" : value === "medium" ? "warning" : "error"
                    }
                    size="small"
                />
            ),
        },
        {
            select: "actions",
            header: "Actions",
            render: (_: any, row: QuestionInterface) => (
                <Box>
                    <IconButton
                        size="small"
                        onClick={() => handleOpenModal(row)}
                        color="primary"
                    >
                        <Edit />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDelete(row._id!)}
                        color="error"
                    >
                        <Delete />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="h4" className='text-white'>Questions</Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenModal()}
                    sx={{
                        backgroundColor: "#fcd307",
                        color: "#000000",
                        "&:hover": {
                            backgroundColor: "#e6be06",
                        },
                    }}
                >
                    Add Question
                </Button>
            </Box>

            <TextField
                fullWidth
                label="Search Questions"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                        color: "#ffffff",
                        "& fieldset": {
                            borderColor: "#ffffff",
                        },
                        "&:hover fieldset": {
                            borderColor: "#ffffff",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#ffffff",
                        },
                    },
                    "& .MuiInputLabel-root": {
                        color: "#ffffff",
                        "&.Mui-focused": {
                            color: "#ffffff",
                        },
                    },
                    "& .MuiInputBase-input": {
                        color: "#ffffff",
                    },
                }}
            />

            <TableComponent
                data={filteredQuestions}
                columns={columns}
            />

            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: "#171717",
                        color: "#ffffff",
                    },
                }}
            >
                <DialogTitle sx={{ color: "#ffffff" }}>
                    {editingQuestion ? "Edit Question" : "Create Question"}
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: "#171717", color: "#ffffff" }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <FormControl fullWidth error={!!errors.subject} required>
                            <InputLabel sx={{ color: "#ffffff" }}>Subject</InputLabel>
                            <Select
                                value={formData.subject}
                                label="Subject"
                                onChange={(e) => {
                                    setFormData({ ...formData, subject: e.target.value });
                                    if (errors.subject) {
                                        setErrors({ ...errors, subject: undefined });
                                    }
                                }}
                                sx={{
                                    color: "#ffffff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: errors.subject ? "#ff0000" : "#ffffff",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: errors.subject ? "#ff0000" : "#ffffff",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: errors.subject ? "#ff0000" : "#ffffff",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "#ffffff",
                                    },
                                }}
                            >
                                {subjects.map((subject) => (
                                    <MenuItem key={subject} value={subject} sx={{ color: "#000000" }}>
                                        {subject}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.subject && (
                                <Typography variant="caption" sx={{ color: "#ff0000", mt: 0.5, ml: 1.75 }}>
                                    {errors.subject}
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl fullWidth error={!!errors.class} required>
                            <InputLabel sx={{ color: "#ffffff" }}>Class</InputLabel>
                            <Select
                                value={formData.class}
                                label="Class"
                                onChange={(e) => {
                                    setFormData({ ...formData, class: e.target.value });
                                    if (errors.class) {
                                        setErrors({ ...errors, class: undefined });
                                    }
                                }}
                                sx={{
                                    color: "#ffffff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: errors.class ? "#ff0000" : "#ffffff",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: errors.class ? "#ff0000" : "#ffffff",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: errors.class ? "#ff0000" : "#ffffff",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "#ffffff",
                                    },
                                }}
                            >
                                {Array.from({ length: 12 }).map((_, i) => {
                                    const v = `class ${i + 1}`;
                                    return (
                                        <MenuItem key={v} value={v} sx={{ color: "#000000" }}>
                                            {v}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {errors.class && (
                                <Typography variant="caption" sx={{ color: "#ff0000", mt: 0.5, ml: 1.75 }}>
                                    {errors.class}
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel sx={{ color: "#ffffff" }}>Question Type</InputLabel>
                            <Select
                                value={formData.questionType}
                                label="Question Type"
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        questionType: e.target.value as "quiz" | "subjective",
                                    })
                                }
                                sx={{
                                    color: "#ffffff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ffffff",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ffffff",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ffffff",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "#ffffff",
                                    },
                                }}
                            >
                                <MenuItem value="quiz" sx={{ color: "#000000" }}>Quiz</MenuItem>
                                <MenuItem value="subjective" sx={{ color: "#000000" }}>Subjective</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Question"
                            multiline
                            rows={3}
                            value={formData.question}
                            onChange={(e) => {
                                setFormData({ ...formData, question: e.target.value });
                                if (errors.question) {
                                    setErrors({ ...errors, question: undefined });
                                }
                            }}
                            error={!!errors.question}
                            helperText={errors.question}
                            required
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    color: "#ffffff",
                                    "& fieldset": {
                                        borderColor: errors.question ? "#ff0000" : "#ffffff",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: errors.question ? "#ff0000" : "#ffffff",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: errors.question ? "#ff0000" : "#ffffff",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "#ffffff",
                                    "&.Mui-focused": {
                                        color: "#ffffff",
                                    },
                                },
                                "& .MuiFormHelperText-root": {
                                    color: "#ff0000",
                                },
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Question Image URL (optional)"
                            value={formData.questionImageUrl}
                            onChange={(e) =>
                                setFormData({ ...formData, questionImageUrl: e.target.value })
                            }
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    color: "#ffffff",
                                    "& fieldset": {
                                        borderColor: "#ffffff",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#ffffff",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#ffffff",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "#ffffff",
                                    "&.Mui-focused": {
                                        color: "#ffffff",
                                    },
                                },
                            }}
                        />

                        {formData.questionType === "quiz" && (
                            <Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ color: "#ffffff" }}>
                                        Options {errors.options && <span style={{ color: "#ff0000" }}>*</span>}
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={handleAddOption}
                                        sx={{
                                            backgroundColor: "#fcd307",
                                            color: "#000000",
                                            "&:hover": {
                                                backgroundColor: "#e6be06",
                                            },
                                        }}
                                    >
                                        Add Option
                                    </Button>
                                </Box>
                                {errors.options && (
                                    <Typography variant="caption" sx={{ color: "#ff0000", mb: 1, display: "block" }}>
                                        {errors.options}
                                    </Typography>
                                )}
                                {formData.options?.map((option, index) => (
                                    <Box key={index} sx={{ display: "flex", gap: 1, mb: 1 }}>
                                        <TextField
                                            fullWidth
                                            label={`Option ${index + 1}`}
                                            value={option.optionText}
                                            onChange={(e) => {
                                                handleUpdateOption(index, e.target.value);
                                                if (errors.options) {
                                                    setErrors({ ...errors, options: undefined });
                                                }
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    color: "#ffffff",
                                                    "& fieldset": {
                                                        borderColor: "#ffffff",
                                                    },
                                                    "&:hover fieldset": {
                                                        borderColor: "#ffffff",
                                                    },
                                                    "&.Mui-focused fieldset": {
                                                        borderColor: "#ffffff",
                                                    },
                                                },
                                                "& .MuiInputLabel-root": {
                                                    color: "#ffffff",
                                                    "&.Mui-focused": {
                                                        color: "#ffffff",
                                                    },
                                                },
                                            }}
                                        />
                                        <Button
                                            variant={formData.correctOptionIndexes?.includes(index) ? "contained" : "outlined"}
                                            onClick={() => {
                                                handleToggleCorrectOption(index);
                                                if (errors.correctOptionIndexes) {
                                                    setErrors({ ...errors, correctOptionIndexes: undefined });
                                                }
                                            }}
                                            sx={
                                                formData.correctOptionIndexes?.includes(index)
                                                    ? {
                                                        backgroundColor: "#fcd307",
                                                        color: "#000000",
                                                        borderColor: "#fcd307",
                                                        "&:hover": {
                                                            backgroundColor: "#e6be06",
                                                            borderColor: "#e6be06",
                                                        },
                                                    }
                                                    : {
                                                        borderColor: "#fcd307",
                                                        color: "#fcd307",
                                                        "&:hover": {
                                                            borderColor: "#e6be06",
                                                            backgroundColor: "rgba(252, 211, 7, 0.1)",
                                                        },
                                                    }
                                            }
                                        >
                                            Correct
                                        </Button>
                                        <IconButton onClick={() => handleRemoveOption(index)} color="error">
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                ))}
                                {errors.correctOptionIndexes && (
                                    <Typography variant="caption" sx={{ color: "#ff0000", mt: 1, display: "block" }}>
                                        {errors.correctOptionIndexes}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {formData.questionType === "subjective" && (
                            <TextField
                                fullWidth
                                label="Answer String"
                                multiline
                                rows={3}
                                value={formData.answerString}
                                onChange={(e) => {
                                    setFormData({ ...formData, answerString: e.target.value });
                                    if (errors.answerString) {
                                        setErrors({ ...errors, answerString: undefined });
                                    }
                                }}
                                error={!!errors.answerString}
                                helperText={errors.answerString}
                                required
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        color: "#ffffff",
                                        "& fieldset": {
                                            borderColor: errors.answerString ? "#ff0000" : "#ffffff",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: errors.answerString ? "#ff0000" : "#ffffff",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: errors.answerString ? "#ff0000" : "#ffffff",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#ffffff",
                                        "&.Mui-focused": {
                                            color: "#ffffff",
                                        },
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: "#ff0000",
                                    },
                                }}
                            />
                        )}

                        <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField
                                type="number"
                                label="Marks"
                                value={formData.marks || ""}
                                onChange={(e) => {
                                    const value = e.target.value === "" ? 0 : Number(e.target.value);
                                    setFormData({ ...formData, marks: value });
                                    if (errors.marks) {
                                        setErrors({ ...errors, marks: undefined });
                                    }
                                }}
                                error={!!errors.marks}
                                helperText={errors.marks}
                                required
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        color: "#ffffff",
                                        "& fieldset": {
                                            borderColor: errors.marks ? "#ff0000" : "#ffffff",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: errors.marks ? "#ff0000" : "#ffffff",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: errors.marks ? "#ff0000" : "#ffffff",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#ffffff",
                                        "&.Mui-focused": {
                                            color: "#ffffff",
                                        },
                                    },
                                    "& .MuiInputBase-input": {
                                        color: "#ffffff",
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: "#ff0000",
                                    },
                                    "& input[type=number]": {
                                        "-moz-appearance": "textfield",
                                    },
                                    "& input[type=number]::-webkit-outer-spin-button": {
                                        "-webkit-appearance": "none",
                                        margin: 0,
                                    },
                                    "& input[type=number]::-webkit-inner-spin-button": {
                                        "-webkit-appearance": "none",
                                        margin: 0,
                                    },
                                }}
                            />
                            <TextField
                                type="number"
                                label="Negative Marks (optional)"
                                value={formData.negativeMarks || ""}
                                onChange={(e) => {
                                    const value = e.target.value === "" ? 0 : Number(e.target.value);
                                    setFormData({ ...formData, negativeMarks: value });
                                    if (errors.negativeMarks) {
                                        setErrors({ ...errors, negativeMarks: undefined });
                                    }
                                }}
                                error={!!errors.negativeMarks}
                                helperText={errors.negativeMarks}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        color: "#ffffff",
                                        "& fieldset": {
                                            borderColor: errors.negativeMarks ? "#ff0000" : "#ffffff",
                                        },
                                        "&:hover fieldset": {
                                            borderColor: errors.negativeMarks ? "#ff0000" : "#ffffff",
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: errors.negativeMarks ? "#ff0000" : "#ffffff",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#ffffff",
                                        "&.Mui-focused": {
                                            color: "#ffffff",
                                        },
                                    },
                                    "& .MuiInputBase-input": {
                                        color: "#ffffff",
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: "#ff0000",
                                    },
                                    "& input[type=number]": {
                                        "-moz-appearance": "textfield",
                                    },
                                    "& input[type=number]::-webkit-outer-spin-button": {
                                        "-webkit-appearance": "none",
                                        margin: 0,
                                    },
                                    "& input[type=number]::-webkit-inner-spin-button": {
                                        "-webkit-appearance": "none",
                                        margin: 0,
                                    },
                                }}
                            />
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel sx={{ color: "#ffffff" }}>Difficulty Level</InputLabel>
                            <Select
                                value={formData.difficultyLevel}
                                label="Difficulty Level"
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        difficultyLevel: e.target.value as "easy" | "medium" | "hard",
                                    })
                                }
                                sx={{
                                    color: "#ffffff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ffffff",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ffffff",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#ffffff",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        color: "#ffffff",
                                    },
                                }}
                            >
                                <MenuItem value="easy" sx={{ color: "#000000" }}>Easy</MenuItem>
                                <MenuItem value="medium" sx={{ color: "#000000" }}>Medium</MenuItem>
                                <MenuItem value="hard" sx={{ color: "#000000" }}>Hard</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: "#171717" }}>
                    <Button
                        onClick={handleCloseModal}
                        sx={{
                            color: "#fcd307",
                            "&:hover": {
                                backgroundColor: "rgba(252, 211, 7, 0.1)",
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            backgroundColor: "#fcd307",
                            color: "#000000",
                            "&:hover": {
                                backgroundColor: "#e6be06",
                            },
                            "&:disabled": {
                                backgroundColor: "#636363",
                                color: "#ffffff",
                            },
                        }}
                    >
                        {editingQuestion ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Questions;
